import type { DatePickerValue } from '@civilgrid/ui/components';

import layerStore from '~/store2/layers';
import type { Contractor, ContractorType, PermittingFormValues } from './permittingFormTypes';
import { getPermittingLegendLayerIdsForPrint } from './permittingMapSnapshot';
import { buildPermittingPrintContentStyles, buildPermittingPrintHostStyles } from './permittingPrintLayoutStyles';
import type { PermitLegendRowModel } from './permittingVisibleLegend';
import { getVisiblePermittingLegendModels } from './permittingVisibleLegend';

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function formatPickerDate(v: DatePickerValue | null): string {
  if (!v || !('date' in v) || typeof v.date !== 'string') return '—';
  try {
    return new Date(v.date).toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch {
    return '—';
  }
}

const WORK_TYPE_ORDER: { id: PermittingFormValues['workTypeRadio']; label: string }[] = [
  { id: 'main', label: 'Main Line' },
  { id: 'service', label: 'Service Line' },
  { id: 'emergency', label: 'Emergency' },
];

const CONTRACTOR_TYPE_LABELS: Record<ContractorType, string> = {
  atmos: 'Atmos',
  contractor: 'Contractor',
  other: 'Other',
};

/** Compact print block for up to 5 contractors — one row per contractor to keep the form on one page. */
function contractorsBlock(contractors: Contractor[]): string {
  if (!contractors.length) {
    return `<div class="contractors-print span-3"><div class="contractor-empty">—</div></div>`;
  }

  const rows = contractors
    .map((c) => {
      const cell = (label: string, value: string) =>
        `<div class="contractor-cell"><span class="contractor-cell-label">${escapeHtml(label)}</span><span class="contractor-cell-value">${escapeHtml(value || '—')}</span></div>`;
      return `<div class="contractor-row">
        ${cell('Type', CONTRACTOR_TYPE_LABELS[c.type] ?? c.type)}
        ${cell('Contractor', c.name)}
        ${cell('Rep', c.repName)}
        ${cell('Email', c.email)}
        ${cell('Phone', c.phone)}
      </div>`;
    })
    .join('');

  return `<div class="contractors-print span-3">${rows}</div>`;
}

function fieldGroup(label: string, value: string, span: 1 | 2 | 3 = 1, multiline = false): string {
  const spanClass = span === 2 ? ' span-2' : span === 3 ? ' span-3' : '';
  const boxClass = multiline ? 'field-box field-box-multiline' : 'field-box';
  return `<div class="field-group${spanClass}">
    <div class="field-label">${escapeHtml(label)}</div>
    <div class="${boxClass}">${escapeHtml(value || '—')}</div>
  </div>`;
}

function sectionTitle(text: string): string {
  return `<div class="section-title span-3">${escapeHtml(text)}</div>`;
}

function radioRow(selected: string, options: { id: string; label: string }[]): string {
  const items = options
    .map((o) => {
      const on = o.id === selected ? ' on' : '';
      return `<span class="radio-item"><span class="radio-dot${on}"></span>${escapeHtml(o.label)}</span>`;
    })
    .join('');
  return `<div class="radio-line">${items}</div>`;
}

function isSafeMapDataUrl(url: string | null | undefined): url is string {
  return (
    typeof url === 'string' && (url.startsWith('data:image/png;base64,') || url.startsWith('data:image/jpeg;base64,'))
  );
}

/** Threshold above which the print legend overflows the 6-column row and is moved to its own page. */
export const PERMITTING_PRINT_LEGEND_PAGE_OVERFLOW_THRESHOLD = 6;

export type PermittingPrintLegend = {
  html: string;
  /** Total legend blocks rendered (project + visible utility layers). */
  blockCount: number;
};

/** Legend entries for **visible** permitting utility layers + project swatch (aligned with on-map condensed legend). */
export function buildPermittingPrintLegendHtml(): PermittingPrintLegend {
  const tilesConfig = layerStore.tilesConfig;
  if (!tilesConfig) {
    return { html: '<p class="legend-unavailable">Utility legend unavailable.</p>', blockCount: 0 };
  }

  const models = getVisiblePermittingLegendModels(tilesConfig, getPermittingLegendLayerIdsForPrint());

  const blocks: string[] = [
    `<div class="legend-layer-block">
      <div class="legend-block-title">${escapeHtml('Project')}</div>
      <div class="legend-row">
        <span class="legend-swatch legend-swatch-project" aria-hidden="true"></span>
        <span class="legend-row-label">${escapeHtml('Project area')}</span>
      </div>
    </div>`,
  ];

  for (const layer of models) {
    const title = escapeHtml(layer.title);
    const rows = layer.rows
      .map((leg: PermitLegendRowModel) => {
        const label = escapeHtml(leg.label);
        return `<div class="legend-row">
          <img class="legend-thumb" src="data:image/jpeg;base64,${leg.imageData}" width="${leg.width}" height="${leg.height}" alt="" />
          <span class="legend-row-label">${label}</span>
        </div>`;
      })
      .join('');

    blocks.push(`<div class="legend-layer-block">
      <div class="legend-block-title">${title}</div>
      ${rows}
    </div>`);
  }

  return {
    html: `<div class="permitting-print-legend">${blocks.join('')}</div>`,
    blockCount: blocks.length,
  };
}

export function buildPrintInnerHtml(
  data: PermittingFormValues,
  displayProjectId: string,
  mapDataUrl: string | null,
  legendHtml: string,
  legendBlockCount: number,
  scaleHtml: string,
  northArrowHtml: string,
): string {
  const mapBlock = isSafeMapDataUrl(mapDataUrl)
    ? `<img class="map-img" src="${mapDataUrl}" alt="Permit area map preview" />`
    : `<div class="map-placeholder">Map preview unavailable</div>`;

  const legendOverflowsPage = legendBlockCount > PERMITTING_PRINT_LEGEND_PAGE_OVERFLOW_THRESHOLD;
  const inlineLegendBlock = legendOverflowsPage
    ? ''
    : `<div class="map-legend-below" aria-hidden="true">${
        legendHtml.trim() ? legendHtml : '<span class="map-legend-fallback" aria-hidden="true"></span>'
      }</div>`;
  const standaloneLegendPage = legendOverflowsPage
    ? `<section class="permitting-legend-page">
  <div class="permitting-legend-page-content">${legendHtml}</div>
  <div class="footer-print permitting-legend-page-footer">
    <span class="footer-left">Created in CivilGrid</span>
    <span class="footer-right">Project ID: ${escapeHtml(displayProjectId)}</span>
  </div>
</section>`
    : '';

  const mapSectionHtml = `<div class="map-section">
    <div class="map-frame">
      ${mapBlock}
      <div class="map-north-overlay" aria-hidden="true">${
        northArrowHtml.trim() ? northArrowHtml : '<span class="map-north-fallback" aria-hidden="true"></span>'
      }</div>
      <div class="map-scale-overlay" aria-hidden="true">${
        scaleHtml
          ? `<div class="print-scale-host">${scaleHtml}</div>`
          : '<span class="map-scale-fallback">Scale unavailable</span>'
      }</div>
    </div>
    ${inlineLegendBlock}
  </div>`;

  // Optionally move the map (and its legend) to its own page to keep the form on a single page.
  const inlineMapSection = data.mapOnSeparatePage ? '' : mapSectionHtml;
  const standaloneMapPage = data.mapOnSeparatePage
    ? `<section class="permitting-map-page">
  ${mapSectionHtml}
  <div class="footer-print permitting-map-page-footer">
    <span class="footer-left">Created in CivilGrid</span>
    <span class="footer-right">Project ID: ${escapeHtml(displayProjectId)}</span>
  </div>
</section>`
    : '';

  return `
<div class="letter">
  <div class="form-grid">
    <h1 class="doc-title span-3">Atmos Permitting Project Information Form</h1>

    ${fieldGroup('Permit submission date', formatPickerDate(data.permitSubmissionDate), 1)}
    ${fieldGroup('Work start date', formatPickerDate(data.workStartDate), 1)}
    ${fieldGroup('Work end date', formatPickerDate(data.workEndDate), 1)}

    <div class="print-field-block-spacer" aria-hidden="true"></div>
    ${fieldGroup('Permit submission contact name', data.permitSubmissionContactName, 1)}
    ${fieldGroup('Permit submission contact email', data.permitSubmissionContactEmail, 1)}
    ${fieldGroup('Permit submission contact phone', data.permitSubmissionContactPhone, 1)}

    ${sectionTitle('Location')}
    ${fieldGroup('Address', data.address, 1)}
    ${fieldGroup('Town', data.city, 1)}
    ${fieldGroup('Zip', data.zip, 1)}

    ${sectionTitle('Atmos Contact')}
    ${fieldGroup('Atmos Rep', data.atmosRep, 1)}
    ${fieldGroup('Email', data.atmosEmail, 1)}
    ${fieldGroup('Phone', data.atmosPhone, 1)}

    ${sectionTitle('Project Info')}
    <div class="permitting-project-work-type-row">
      <div class="field-group">
        <div class="field-label">Work type</div>
        ${radioRow(data.workTypeRadio, WORK_TYPE_ORDER)}
        ${fieldGroup('Project number', data.projectNumber, 1)}
      </div>
      ${fieldGroup('Work type (text)', data.workTypeText, 2, true)}
    </div>

    ${sectionTitle('Contractors')}
    ${contractorsBlock(data.contractors)}
  </div>

  ${inlineMapSection}

  <div class="footer-print">
    <span class="footer-left">Created in CivilGrid</span>
    <span class="footer-right">Project ID: ${escapeHtml(displayProjectId)}</span>
  </div>
</div>
${standaloneMapPage}
${standaloneLegendPage}
<section class="restoration-plan-page">
  <h1 class="restoration-plan-title">Atmos Energy &ndash; Site Restoration Plan</h1>

  <h2 class="restoration-plan-heading">1. Purpose</h2>
  <p class="restoration-plan-body">This Site Restoration Plan outlines Atmos Energy&rsquo;s expectations and requirements for restoring all areas affected during construction, maintenance, or repair activities. Restoration applies to all public Right-of-Way (ROW) and any impacted private property, ensuring all sites are returned to equal or better condition than before work began.</p>

  <h2 class="restoration-plan-heading">2. Restoration Commitment</h2>
  <p class="restoration-plan-body">Atmos Energy is committed to restoring all areas impacted by our work activities, including Public ROW, easements, private property, landscaping, vegetation, ground cover, sidewalks, driveways, concrete, asphalt, hardscape surfaces, utility boxes, valves, lids, clean-outs, vaults, appurtenances, irrigation systems, and traffic control devices. All restoration will be completed as promptly as circumstances permit to minimize disruption to the public and property owners.</p>

  <h2 class="restoration-plan-heading">3. Site Cleanup</h2>
  <p class="restoration-plan-body">Excess soil, spoils, and construction materials will be removed daily. Streets, sidewalks, and driveways will be kept free of mud and debris. Work areas will remain clean and safe for the duration of the project.</p>

  <h2 class="restoration-plan-heading">4. Final Inspection</h2>
  <p class="restoration-plan-body">Once restoration is complete, a final walk-through can be conducted with the property owner (if applicable) and/or city representative. Any deficiencies identified will be corrected in a timely manner. Atmos Energy may document site restoration with before-and-after photos.</p>

  <div class="footer-print restoration-plan-footer">
    <span class="footer-left">Created in CivilGrid</span>
    <span class="footer-right">Project ID: ${escapeHtml(displayProjectId)}</span>
  </div>
</section>`;
}

const OVERLAY_ID = 'permitting-print-overlay';
const HIDE_ROOT_STYLE_ID = 'permitting-print-hide-root';
const CONTENT_STYLE_ID = 'permitting-print-content-style';
/** Upper bound on how long printing waits for the print stylesheet's webfont. */
const FONT_WAIT_TIMEOUT_MS = 1_500;

export type PermittingPrintExtras = {
  legendHtml?: string;
  /** Number of legend blocks (project + visible utility layers); drives single-vs-two-page layout. */
  legendBlockCount?: number;
  scaleHtml?: string;
  northArrowHtml?: string;
};

/** Injects print-only markup, opens the browser’s native print preview for this tab, then removes the markup. */
export function printPermittingRequest(
  data: PermittingFormValues,
  /** Human-readable project id (e.g. six-digit `Project.projectId`), not the long URL id. */
  displayProjectId: string,
  mapDataUrl: string | null = null,
  extras: PermittingPrintExtras = {},
): void {
  document.getElementById(OVERLAY_ID)?.remove();
  document.getElementById(HIDE_ROOT_STYLE_ID)?.remove();
  document.getElementById(CONTENT_STYLE_ID)?.remove();

  const inner = buildPrintInnerHtml(
    data,
    displayProjectId,
    mapDataUrl,
    extras.legendHtml ?? '',
    extras.legendBlockCount ?? 0,
    extras.scaleHtml ?? '',
    extras.northArrowHtml ?? '',
  );

  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.setAttribute('aria-hidden', 'true');
  overlay.innerHTML = inner;

  const contentStyle = document.createElement('style');
  contentStyle.id = CONTENT_STYLE_ID;
  contentStyle.textContent = buildPermittingPrintContentStyles(OVERLAY_ID);

  const hideRoot = document.createElement('style');
  hideRoot.id = HIDE_ROOT_STYLE_ID;
  hideRoot.textContent = buildPermittingPrintHostStyles(OVERLAY_ID);

  document.body.appendChild(contentStyle);
  document.body.appendChild(hideRoot);
  document.body.appendChild(overlay);

  let cleaned = false;
  let fallbackTimer: number | undefined;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    window.removeEventListener('afterprint', cleanup);
    if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
    overlay.remove();
    contentStyle.remove();
    hideRoot.remove();
  };

  window.addEventListener('afterprint', cleanup);

  const runPrint = () => {
    window.print();
  };

  const mapImageReady = (): Promise<void> => {
    const img = overlay.querySelector('img.map-img');
    if (!(img instanceof HTMLImageElement)) return Promise.resolve();
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    return img.decode().catch(() => undefined);
  };

  /**
   * The print stylesheet pulls its webfont in over the network via @import, so printing immediately
   * can measure fallback font metrics (wider) and overflow the page box — Safari loses this race
   * more often than Chrome. Capped so a slow or blocked font never stops the print dialog.
   */
  const fontsReady = (): Promise<void> => {
    if (!document.fonts) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const timer = window.setTimeout(resolve, FONT_WAIT_TIMEOUT_MS);
      void document.fonts.ready.then(() => {
        window.clearTimeout(timer);
        resolve();
      });
    });
  };

  const schedulePrint = () => {
    void Promise.all([mapImageReady(), fontsReady()]).then(() => requestAnimationFrame(runPrint));
  };

  requestAnimationFrame(() => requestAnimationFrame(schedulePrint));

  fallbackTimer = window.setTimeout(() => {
    if (document.getElementById(OVERLAY_ID)) {
      cleanup();
    }
  }, 120_000);
}
